import { createContext, useContext, useEffect, useMemo, useReducer, type ReactNode } from 'react';
import type { PaymentMethod, Product } from '@bazaar/shared';
import {
  cartReducer,
  computeTotals,
  initialCartState,
  isDiscountValid,
  type CartState,
  type CartTotals,
} from './cartReducer.js';

const STORAGE_KEY = 'bazaar.cart.v1';

function loadInitialState(): CartState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return initialCartState;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as CartState).lines)) {
      return { ...initialCartState, ...(parsed as CartState) };
    }
  } catch {
    // Corrupt or inaccessible storage (private browsing, etc.) - start fresh.
  }
  return initialCartState;
}

interface CartContextValue {
  state: CartState;
  totals: CartTotals;
  discountValid: boolean;
  addItem: (product: Product) => void;
  incrementItem: (productId: number) => void;
  decrementItem: (productId: number) => void;
  removeItem: (productId: number) => void;
  setQty: (productId: number, qty: number) => void;
  setPriceOverride: (productId: number, cents: number | null) => void;
  setDiscountCents: (cents: number | null) => void;
  setPaymentMethod: (method: PaymentMethod) => void;
  setNote: (note: string) => void;
  updateStock: (productId: number, stockQty: number) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, undefined, loadInitialState);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore quota/availability errors - the cart just won't survive a refresh.
    }
  }, [state]);

  const totals = useMemo(() => computeTotals(state), [state]);
  const discountValid = useMemo(() => isDiscountValid(state), [state]);

  const value = useMemo<CartContextValue>(
    () => ({
      state,
      totals,
      discountValid,
      addItem: (product) =>
        dispatch({
          type: 'ADD',
          product: {
            id: product.id,
            name: product.name,
            code: product.code,
            thumbUrl: product.thumbUrl,
            priceCents: product.priceCents,
            stockQty: product.stockQty,
          },
        }),
      incrementItem: (productId) => dispatch({ type: 'INCREMENT', productId }),
      decrementItem: (productId) => dispatch({ type: 'DECREMENT', productId }),
      removeItem: (productId) => dispatch({ type: 'REMOVE', productId }),
      setQty: (productId, qty) => dispatch({ type: 'SET_QTY', productId, qty }),
      setPriceOverride: (productId, cents) => dispatch({ type: 'SET_PRICE_OVERRIDE', productId, cents }),
      setDiscountCents: (cents) => dispatch({ type: 'SET_DISCOUNT', cents }),
      setPaymentMethod: (method) => dispatch({ type: 'SET_PAYMENT_METHOD', method }),
      setNote: (note) => dispatch({ type: 'SET_NOTE', note }),
      updateStock: (productId, stockQty) => dispatch({ type: 'UPDATE_STOCK', productId, stockQty }),
      clear: () => dispatch({ type: 'CLEAR' }),
    }),
    [state, totals, discountValid],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}
